import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, Drawer } from '@mui/material';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import CustomTextField from '../../form/CustomTextField';
import CustomSelectField from '../../form/CustomSelectField';
import { createAddressesAction, updateAddressesAction } from '../../redux/actions/address';
import strings from '../../i18n/language';
import _ from 'lodash';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

const BRAND = '#3b95e8';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';

const DAYS = [
  { key: 1, label: 'Lunes' },
  { key: 2, label: 'Martes' },
  { key: 3, label: 'Miércoles' },
  { key: 4, label: 'Jueves' },
  { key: 5, label: 'Viernes' },
  { key: 6, label: 'Sábado' },
  { key: 7, label: 'Domingo' },
];

const HOURS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return { value: i / 2, label: `${String(h).padStart(2, '0')}:${m}` };
});

const formatHour = (h) =>
  `${Math.floor(h)}h${Math.floor((h % 1) * 60).toString().padStart(2, '0')}`;

const selectStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: 8,
  color: '#e8f0fe',
  fontSize: 12,
  padding: '5px 6px',
  cursor: 'pointer',
  outline: 'none',
  width: 80,
};

const TimeSelect = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(parseFloat(e.target.value))}
    style={selectStyle}
  >
    {HOURS.map((h) => (
      <option key={h.value} value={h.value}>{h.label}</option>
    ))}
  </select>
);

const DayRow = ({ dayName, slots, onChange }) => {
  const isOpen = slots.length > 0;

  const toggle = () => onChange(isOpen ? [] : [{ open: 8, close: 20 }]);

  const updateSlot = (index, field, value) => {
    const next = [...slots];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const addSlot = () => {
    const last = slots[slots.length - 1];
    onChange([...slots, { open: last ? last.close : 14, close: last ? Math.min(last.close + 4, 23.5) : 18 }]);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderBottom: `1px solid rgba(255,255,255,0.04)` }}>

      {/* Day + toggle */}
      <div style={{ width: 100, flexShrink: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: isOpen ? '#e8f0fe' : MUTED, marginBottom: 4 }}>
          {dayName}
        </div>
        <button onClick={toggle} style={{
          background: isOpen ? 'rgba(91,191,181,0.12)' : 'rgba(255,255,255,0.04)',
          color: isOpen ? '#5bbfb5' : MUTED,
          border: `1px solid ${isOpen ? 'rgba(91,191,181,0.25)' : 'rgba(255,255,255,0.07)'}`,
          borderRadius: 20, padding: '2px 10px',
          fontSize: 10, fontWeight: 700, cursor: 'pointer',
          letterSpacing: 0.4, textTransform: 'uppercase',
        }}>
          {isOpen ? 'Abierto' : 'Cerrado'}
        </button>
      </div>

      {/* Slots */}
      <div style={{ flex: 1 }}>
        {isOpen ? (
          <>
            {slots.map((slot, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <TimeSelect value={slot.open} onChange={(v) => updateSlot(i, 'open', v)} />
                <span style={{ color: MUTED, fontSize: 12 }}>–</span>
                <TimeSelect value={slot.close} onChange={(v) => updateSlot(i, 'close', v)} />
                {slots.length > 1 && (
                  <button onClick={() => onChange(slots.filter((_, j) => j !== i))} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'rgba(224,92,92,0.5)', padding: 0, display: 'flex', alignItems: 'center',
                  }}>
                    <CloseRoundedIcon style={{ fontSize: 13 }} />
                  </button>
                )}
              </div>
            ))}
            <button onClick={addSlot} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(59,149,232,0.6)', fontSize: 11, fontWeight: 600,
              padding: 0, display: 'flex', alignItems: 'center', gap: 3, marginTop: 2,
            }}>
              <AddRoundedIcon style={{ fontSize: 12 }} /> añadir
            </button>
          </>
        ) : (
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.15)', paddingTop: 2, display: 'block' }}>—</span>
        )}
      </div>
    </div>
  );
};

const FieldRow = ({ tooltip, children }) => (
  <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ flex: 1 }}>{children}</div>
    {tooltip && (
      <Tooltip title={tooltip} placement="left">
        <InfoOutlinedIcon style={{ fontSize: 13, color: MUTED, cursor: 'help', flexShrink: 0 }} />
      </Tooltip>
    )}
  </div>
);

const emptyOpening = () => ({ 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] });

const LocalsEdit = ({ open, onClose }) => {
  const dispatch = useDispatch();

  const [label, setLabel] = useState(null);
  const [errorLabel, setErrorLabel] = useState(null);
  const [address, setAddress] = useState(null);
  const [errorAddress, setErrorAddress] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [errorPhoneNumber, setErrorPhoneNumber] = useState(null);
  const [addressId, setAddressId] = useState(null);
  const [errorAddressId, setErrorAddressId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [originalAddress, setOriginalAddress] = useState(null);
  const [opening, setOpening] = useState(emptyOpening());

  const addressObject = useSelector((state) => state.address.address);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);

  const isEdit = !!(addressObject && addressObject.id);

  useEffect(() => {
    if (!open) return;
    if (addressObject) {
      setOriginalAddress(addressObject);
      setLabel(addressObject.label);
      setAddress(addressObject.address);
      setPhoneNumber(addressObject.phoneNumber);
      setAddressId(addressObject.addressId);
      setOpening(addressObject.opening ? JSON.parse(addressObject.opening) : emptyOpening());
    } else {
      setOriginalAddress(null);
      setLabel(null);
      setAddress(null);
      setPhoneNumber(null);
      setAddressId(null);
      setOpening(emptyOpening());
    }
    setDirty(false);
  }, [open, addressObject]);

  useEffect(() => {
    if (originalAddress) {
      setDirty(
        addressId !== originalAddress.addressId ||
        address !== originalAddress.address ||
        phoneNumber !== originalAddress.phoneNumber ||
        JSON.stringify(opening) !== originalAddress.opening
      );
    } else if (addressId || address || phoneNumber) {
      setDirty(true);
    } else {
      setDirty(false);
    }
  }, [addressId, address, phoneNumber, opening, originalAddress]);

  useEffect(() => {
    const re = /^[a-z0-9\sáéíóúñüäöüß.,;:!?'"\-()%#]+$/i;
    setErrorLabel(!(label && label.length > 4 && label.length < 30 && re.test(label)));
  }, [label]);

  useEffect(() => {
    const re = /^[a-z0-9\sáéíóúñüäöüß.,;:!?'"\-()%#]+$/i;
    setErrorAddress(!(address && address.length > 4 && address.length < 60 && re.test(address)));
  }, [address]);

  useEffect(() => {
    if (phoneNumber) setErrorPhoneNumber(!/^(tel:|whatsapp:)\+?[0-9]{1,15}$/i.test(phoneNumber));
    else setErrorPhoneNumber(false);
  }, [phoneNumber]);

  useEffect(() => {
    setErrorAddressId(!(addressId && /^[A-Za-z0-9]{16,20}$/i.test(addressId)));
  }, [addressId]);

  const updateDaySlots = (dayKey, slots) => {
    setOpening((prev) => ({ ...prev, [dayKey]: slots }));
  };

  const save = () => {
    if (errorLabel) { toast.error(strings.LABEL_LOCALS_NAME_ERROR); return; }
    if (errorAddress) { toast.error(strings.LABEL_LOCALS_ADDRESS_ERROR); return; }
    if (errorPhoneNumber) { toast.error(strings.LABEL_LOCALS_PHONE_ERROR); return; }
    if (errorAddressId) { toast.error(strings.LABEL_LOCALS_ADDRESS_ID_ERROR); return; }
    if (isEdit) {
      dispatch(updateAddressesAction({ ...addressObject, label, address, phoneNumber, addressId, opening }));
    } else {
      dispatch(createAddressesAction({ label, address, phoneNumber, addressId, company_id: selectedCompany.id, opening }));
    }
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 520,
          background: '#0d1b36',
          borderLeft: `1px solid ${BORDER}`,
          boxShadow: '-24px 0 60px rgba(0,0,0,0.4)',
        },
      }}
    >
      <ToastContainer position="bottom-right" />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe' }}>
          {isEdit ? 'Editar local' : 'Nuevo local'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <button onClick={onClose} style={{
            padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
            background: 'rgba(255,255,255,0.05)', color: MUTED,
            border: `1px solid ${BORDER}`, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <CloseRoundedIcon style={{ fontSize: 15 }} /> Cancelar
          </button>
          <button onClick={save} disabled={!dirty} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: dirty ? BRAND : 'rgba(59,149,232,0.15)',
            color: dirty ? '#fff' : 'rgba(59,149,232,0.4)',
            border: 'none', cursor: dirty ? 'pointer' : 'default',
            boxShadow: dirty ? '0 4px 14px rgba(59,149,232,0.3)' : 'none',
            transition: 'all 0.2s',
          }}>
            <SaveRoundedIcon style={{ fontSize: 15 }} /> Guardar
          </button>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ overflowY: 'auto' }}>

        {/* Info */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 16 }}>
            Información
          </div>
          <FieldRow tooltip={strings.LABEL_LOCALS_NAME_TOOLTIP}>
            <CustomTextField id="label" label={strings.LABEL_LOCALS_NAME} value={label} onChange={setLabel} margin="none" autoFocus />
          </FieldRow>
          <FieldRow tooltip={strings.LABEL_LOCALS_ADDRESS_TOOLTIP}>
            <CustomTextField id="address" label={strings.LABEL_LOCALS_ADDRESS} value={address} onChange={setAddress} margin="none" />
          </FieldRow>
          <FieldRow tooltip={strings.LABEL_LOCALS_PHONE_TOOLTIP}>
            <CustomTextField id="phone" label={strings.LABEL_LOCALS_PHONE} value={phoneNumber} onChange={setPhoneNumber} margin="none" />
          </FieldRow>
          <FieldRow tooltip={strings.LABEL_LOCALS_ADDRESS_ID_TOOLTIP}>
            <CustomTextField id="addressId" label={strings.LABEL_LOCALS_ADDRESS_ID} value={addressId} onChange={setAddressId} margin="none" />
          </FieldRow>
        </Box>

        {/* Hours */}
        <Box sx={{ px: 3, pt: 2, pb: 3, borderTop: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
            {strings.LABEL_SETTINGS_SELECT_OPENING_HOURS}
          </div>
          {DAYS.map((d) => (
            <DayRow
              key={d.key}
              dayName={d.label}
              slots={opening[d.key] || []}
              onChange={(slots) => updateDaySlots(d.key, slots)}
            />
          ))}
        </Box>
      </Box>
    </Drawer>
  );
};

export default LocalsEdit;
